import { Avatar, Col, Input, Modal, Row, Spin, Table, Tabs, message } from "antd";
import { RightOutlined, UserOutlined } from "@ant-design/icons";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import UserFloatingButton from "pages/Performance/UserFloatingButton";
import userFloatCycle from "assets/userFloatCycle.svg";
import KpiHistoryBadge from "./KpiHistoryBadge";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { useSelector } from "react-redux";
import { getAllDepartmentList, getLocations, getUserInfo, getUserList } from "redux/selectors";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

const { TextArea } = Input;

const Wrap = styled.div`
  .breadcrumb {
    display: flex;
    align-items: center;
  }
  .perspective-title {
    font-weight: 600;
    margin: 16px 0 8px;
  }
  .footer {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: end;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin: 20px -20px 0;
  }
  .ant-table-thead > tr > th {
    background: #C0CDE2 !important;
    font-weight: 600;
    font-size: 13px;
    color: #000000;
  }
  .ant-table-sticky-holder {
    background: #fff;
    z-index: 3;
  }
  .ant-table-tbody > tr > td {
    font-size: 13px;
    padding: 8px 12px;
  }
  .planning-group-row > td {
    background: #fafafa;
  }
`;

const formatDate = (value) => (value ? dayjs(value).format("Do MMM YYYY") : "-");

const textAreaProps = { autoSize: { minRows: 1, maxRows: 4 } };

const rowKeyOf = (perspective, sno) => `${perspective}-${sno}`;

const groupByPerspective = (perspectives = []) => {
  const grouped = {};
  perspectives.forEach((item) => {
    const key = item.perspective || "Other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  return grouped;
};

const ReviewProgress = () => {
  const history = useHistory();
  const location = useLocation();
  const contractid = location.state?.contractid;
  // Set only by the appraisee's Mine list when "Self Progress Review" is on in
  // People - the appraisee can then complete the checkpoint too. My Team
  // never sets it, so the appraiser's review flow is unchanged.
  const selfReview = Boolean(location.state?.selfReview);
  const userInfo = useSelector(getUserInfo);

  const [contract, setContract] = useState(null);
  const [rowsByPerspective, setRowsByPerspective] = useState({});
  const [rolloverRows, setRolloverRows] = useState([]);
  const [trainingRows, setTrainingRows] = useState([]);
  // Keyed by checkpoint label (Q1 / Mid Year / Q3), same pattern as
  // row.progressupdates - Appraiser's Comment is specific to each review
  // cycle and must NOT carry forward from a previous checkpoint into the
  // next one.
  const [appraisercomments, setAppraisercomments] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [kpiViewOpen, setKpiViewOpen] = useState(false);
  const [activeReviewTab, setActiveReviewTab] = useState("planning");
  const [showErrors, setShowErrors] = useState(false);

  const userList = useSelector(getUserList);
  const departmentList = useSelector(getAllDepartmentList);
  const locationList = useSelector(getLocations);
  const appraiseeUser = userList.find((user) => user.userid === contract?.appraiseeuserid);
  const appraiseeDepartment = departmentList.find(
    (dept) => dept.department_id === appraiseeUser?.basedepartmentid
  )?.department_name;
  const appraiseeLocation = locationList.find(
    (loc) => loc.location_id === appraiseeUser?.baselocationid
  )?.location_name;

  useEffect(() => {
    // The sticky footer is clamped to the padding edge of this scroll
    // container, so the layout's own bottom padding always shows as a gap
    // below it - drop it to 0 while this page is mounted and restore after.
    const main = document.querySelector(".ant-layout-content");
    if (!main) return;
    const previousPaddingBottom = main.style.paddingBottom;
    main.style.paddingBottom = "0";
    return () => {
      main.style.paddingBottom = previousPaddingBottom;
    };
  }, []);

  const fetchContract = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/contract/${contractid}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) {
        setContract(data);
        setRowsByPerspective(groupByPerspective(data?.perspectives));
        setRolloverRows(data?.rolloverresponsibilities || []);
        setTrainingRows(data?.trainingdevelopmentplan || []);
        setAppraisercomments(data?.appraisercomments || {});
      }
    } catch (error) {
      console.error("Error fetching contract:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contractid) fetchContract();
  }, [contractid]);

  const nextCheckpoint = (contract?.progressreviewdates || []).find((item) => !item.completed);
  const currentCheckpoint = nextCheckpoint?.label;
  const completedCheckpoints = (contract?.progressreviewdates || []).filter(
    (item) => item.completed
  );
  const lastCompletedCheckpoint = completedCheckpoints[completedCheckpoints.length - 1];

  // Each checkpoint (Q1 / Mid Year / Q3) keeps its own Achievements/Corrective-
  // actions entry under progressupdates[label], so completing Q1 doesn't
  // overwrite/prefill Mid Year with Q1's answers - only the current
  // checkpoint's slot is ever read or written here. Training's "taken" field
  // now follows the same per-checkpoint pattern (progressupdates[label].taken)
  // so it doesn't leak between checkpoints either.
  const setCheckpointField = (row, field, value) => ({
    ...row,
    progressupdates: {
      ...(row.progressupdates || {}),
      [currentCheckpoint]: { ...(row.progressupdates?.[currentCheckpoint] || {}), [field]: value },
    },
  });

  const checkpointValue = (row, field, key = viewCheckpoint) => row.progressupdates?.[key]?.[field] || "";

  const updateRow = (perspective, index, field, value) => {
    setRowsByPerspective((prev) => {
      const rows = [...prev[perspective]];
      rows[index] = setCheckpointField(rows[index], field, value);
      return { ...prev, [perspective]: rows };
    });
  };

  const updateRolloverRow = (index, field, value) => {
    setRolloverRows((prev) => {
      const rows = [...prev];
      rows[index] = setCheckpointField(rows[index], field, value);
      return rows;
    });
  };

  const updateTrainingRow = (index, field, value) => {
    setTrainingRows((prev) => {
      const rows = [...prev];
      rows[index] = setCheckpointField(rows[index], field, value);
      return rows;
    });
  };

  // "accepted" (non-self-KPI: no separate KPI-review step) or
  // "inprogressreview" (between checkpoints) - once the next checkpoint's
  // date arrives it should be actionable here too, computed on read rather
  // than via a scheduled backend job flipping the stored status.
  const isCheckpointDue =
    ["accepted", "inprogressreview"].includes(contract?.planningstatus) &&
    Boolean(nextCheckpoint?.date) &&
    new Date(nextCheckpoint.date) <= new Date();
  // Self Progress Review appraisees can also review while their submitted
  // KPI is still with the appraiser for KPI review.
  const isSelfReviewDue =
    selfReview &&
    contract?.planningstatus === "awaitingkpireview" &&
    Boolean(nextCheckpoint?.date) &&
    new Date(nextCheckpoint.date) <= new Date();
  const isReviewDue =
    contract?.planningstatus === "awaitingprogressreview" || isCheckpointDue || isSelfReviewDue;
  const isAppraiseeViewer =
    Boolean(contract?.appraiseeuserid) && contract.appraiseeuserid === userInfo?.userid;
  // Only the party responsible for this checkpoint can fill it in.
  const canRespond = isReviewDue && (selfReview ? isAppraiseeViewer : !isAppraiseeViewer);
  const isWaitingOnAppraisee = isReviewDue && selfReview && !isAppraiseeViewer;
  const commentLabel = selfReview ? "Appraisee's Comment" : "Appraiser's Comment";
  const backToList = () =>
    history.push("/performance", {
      id: "kpiplanning",
      subTab: isAppraiseeViewer ? "mine" : "myteam",
    });
  // Once every checkpoint is done, there's no single "current" review left
  // to focus the page on - show the full plan with a tab per checkpoint
  // (like AwaitingAppraiseeResponse.js) instead of the floating-button
  // viewer and the last-checkpoint-only framing used while still in progress.
  const isReviewsClosed = contract?.planningstatus === "reviewsclosed";
  // While waiting for the next checkpoint's date to arrive, there's nothing
  // to fill in for it yet - show the last *completed* checkpoint's data
  // instead of the next (empty) one, so the page doesn't render blank
  // Achievements/Corrective-actions columns under a checkpoint nobody has
  // reached.
  const viewCheckpoint = canRespond ? currentCheckpoint : lastCompletedCheckpoint?.label;

  // The 4 perspectives render as ONE continuous table (one shared sticky
  // header) - perspective titles are injected as full-width rows within the
  // same table via colSpan, rather than each perspective having its own
  // separate Table/header.
  const buildPlanningRows = () => {
    const rows = [];
    Object.entries(rowsByPerspective).forEach(([perspective, items]) => {
      rows.push({ key: `group-${perspective}`, isGroupHeader: true, perspective });
      items.forEach((row, index) =>
        rows.push({ ...row, key: rowKeyOf(perspective, row.sno), localIndex: index, perspective })
      );
    });
    return rows;
  };

  const fullSpanCell = (record) => (record.isGroupHeader ? { colSpan: 6 } : {});
  const hiddenCell = (record) => (record.isGroupHeader ? { colSpan: 0 } : {});

  const planningColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      onCell: fullSpanCell,
      render: (_, record) =>
        record.isGroupHeader ? (
          <div className="perspective-title m-0">{record.perspective}</div>
        ) : (
          record.sno
        ),
    },
    {
      title: "Measurement Dimensions",
      dataIndex: "measurementdimension",
      key: "measurementdimension",
      onCell: hiddenCell,
    },
    { title: "Objectives", dataIndex: "objectives", key: "objectives", onCell: hiddenCell },
    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110, onCell: hiddenCell },
    {
      title: "Achievements / Progress to date *",
      key: "achievements",
      onCell: hiddenCell,
      render: (_, record) =>
        canRespond ? (
          <TextArea
            {...textAreaProps}
            value={checkpointValue(record, "achievements")}
            status={showErrors && !checkpointValue(record, "achievements") ? "error" : undefined}
            onChange={(e) =>
              updateRow(record.perspective, record.localIndex, "achievements", e.target.value)
            }
          />
        ) : (
          checkpointValue(record, "achievements") || "-"
        ),
    },
    {
      title: "Emphasis / Corrective actions *",
      key: "correctiveactions",
      onCell: hiddenCell,
      render: (_, record) =>
        canRespond ? (
          <TextArea
            {...textAreaProps}
            value={checkpointValue(record, "correctiveactions")}
            status={showErrors && !checkpointValue(record, "correctiveactions") ? "error" : undefined}
            onChange={(e) =>
              updateRow(record.perspective, record.localIndex, "correctiveactions", e.target.value)
            }
          />
        ) : (
          checkpointValue(record, "correctiveactions") || "-"
        ),
    },
  ];

  const rolloverColumns = [
    { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
    { title: "Objectives", dataIndex: "objectives", key: "objectives" },
    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110 },
    {
      title: "Achievements / Progress to date *",
      key: "achievements",
      render: (_, row, index) =>
        canRespond ? (
          <TextArea
            {...textAreaProps}
            value={checkpointValue(row, "achievements")}
            status={showErrors && !checkpointValue(row, "achievements") ? "error" : undefined}
            onChange={(e) => updateRolloverRow(index, "achievements", e.target.value)}
          />
        ) : (
          checkpointValue(row, "achievements") || "-"
        ),
    },
    {
      title: "Emphasis / Corrective actions *",
      key: "correctiveactions",
      render: (_, row, index) =>
        canRespond ? (
          <TextArea
            {...textAreaProps}
            value={checkpointValue(row, "correctiveactions")}
            status={showErrors && !checkpointValue(row, "correctiveactions") ? "error" : undefined}
            onChange={(e) => updateRolloverRow(index, "correctiveactions", e.target.value)}
          />
        ) : (
          checkpointValue(row, "correctiveactions") || "-"
        ),
    },
  ];

  // Training's "taken" field is now checkpoint-scoped just like Achievements
  // / Corrective actions - `checkpointKey` picks which checkpoint's value to
  // read/edit, and `editable` decides whether it's a live TextArea or plain
  // text. Reused for the open page (checkpointKey = viewCheckpoint), the
  // closed page and the KPI modal (checkpointKey = activeReviewTab, so
  // switching tabs shows that checkpoint's own data instead of bleeding
  // Q1's answer into every later view).
  const buildTrainingColumns = ({ editable, checkpointKey }) => [
    { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
    { title: "Planned areas for strengthening", dataIndex: "skill", key: "skill" },
    { title: "Method of development", dataIndex: "method", key: "method" },
    { title: "Planned / Expected quarter", dataIndex: "quarter", key: "quarter", width: 150 },
    {
      title: "Development / training taken (also indicate method & place)",
      key: "taken",
      render: (_, row, index) =>
        editable ? (
          <TextArea
            {...textAreaProps}
            value={checkpointValue(row, "taken", checkpointKey)}
            onChange={(e) => updateTrainingRow(index, "taken", e.target.value)}
          />
        ) : (
          checkpointValue(row, "taken", checkpointKey) || "-"
        ),
    },
  ];

  // "KPI Planning & Progress Reviews" modal (opened via the floating button) -
  // same read-only Planning view AttendContract.js/ReviewAppraisal.js show,
  // projecting either the original plan or a completed checkpoint's
  // achievements / corrective actions depending on the selected tab.
  const isKpiModalReviewTab = activeReviewTab !== "planning";
  const kpiModalColSpan = isKpiModalReviewTab ? 6 : 5;
  const kpiModalFullSpanCell = (record) => (record.isGroupHeader ? { colSpan: kpiModalColSpan } : {});
  const kpiModalHiddenCell = (record) => (record.isGroupHeader ? { colSpan: 0 } : {});
  const kpiModalColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      onCell: kpiModalFullSpanCell,
      render: (_, record) =>
        record.isGroupHeader ? (
          <div className="perspective-title m-0">
            {record.perspective === "Rollover"
              ? "Rollover Responsibility from Previous Appraisal"
              : record.perspective}
          </div>
        ) : (
          record.sno
        ),
    },
    {
      title: "Measurement Dimensions",
      dataIndex: "measurementdimension",
      key: "measurementdimension",
      onCell: kpiModalHiddenCell,
    },
    { title: "Objectives", dataIndex: "objectives", key: "objectives", onCell: kpiModalHiddenCell },
    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110, onCell: kpiModalHiddenCell },
    ...(isKpiModalReviewTab
      ? [
          {
            title: "Achievements / Progress to date",
            key: "achievements",
            onCell: kpiModalHiddenCell,
            render: (_, record) => record.progressupdates?.[activeReviewTab]?.achievements || "-",
          },
          {
            title: "Emphasis / Corrective actions",
            key: "correctiveactions",
            onCell: kpiModalHiddenCell,
            render: (_, record) => record.progressupdates?.[activeReviewTab]?.correctiveactions || "-",
          },
        ]
      : [
          {
            title: "Planning Assumptions / Comments",
            dataIndex: "comments",
            key: "comments",
            onCell: kpiModalHiddenCell,
            render: (val) => val || "-",
          },
        ]),
  ];
  const kpiModalGrouped = groupByPerspective(contract?.perspectives);
  const kpiModalRolloverRows = contract?.rolloverresponsibilities || [];
  if (kpiModalRolloverRows.length > 0) {
    kpiModalGrouped.Rollover = kpiModalRolloverRows.map((row) => ({ ...row, perspective: "Rollover" }));
  }
  const buildKpiModalRows = (grouped) => {
    const rows = [];
    Object.entries(grouped).forEach(([perspective, items]) => {
      rows.push({ key: `kpimodal-group-${perspective}`, isGroupHeader: true, perspective });
      items.forEach((row) =>
        rows.push({ ...row, key: `kpimodal-${perspective}-${row.sno}`, perspective })
      );
    });
    return rows;
  };
  const kpiModalTabItems = [
    { key: "planning", label: "KPI Planning" },
    ...(contract?.progressreviewdates || [])
      .filter((item) => item.completed)
      .map((item) => ({ key: item.label, label: `${item.label} Review` })),
  ];

  const validateProgress = () => {
    const missing = new Set();
    Object.entries(rowsByPerspective).forEach(([perspective, rows]) => {
      rows.forEach((row) => {
        if (!checkpointValue(row, "achievements") || !checkpointValue(row, "correctiveactions")) {
          missing.add(perspective);
        }
      });
    });
    if (
      rolloverRows.some(
        (row) => !checkpointValue(row, "achievements") || !checkpointValue(row, "correctiveactions")
      )
    ) {
      missing.add("Rollover Responsibility");
    }
    return [...missing];
  };

  const onComplete = () => {
    const missing = validateProgress();
    if (missing.length) {
      setShowErrors(true);
      message.error(
        `Achievements / Progress to date and Emphasis / Corrective actions are mandatory for: ${missing.join(", ")}`
      );
      return;
    }
    setShowErrors(false);
    Modal.confirm({
      title: "Mark this progress review as complete?",
      icon: null,
      centered: true,
      okText: "Yes, Complete",
      cancelText: "No, Cancel",
      okButtonProps: { type: "primary" },
      onOk: async () => {
        const perspectives = Object.values(rowsByPerspective).flat();
        setSubmitLoading(true);
        try {
          const { code } = await CALL_API(
            `performance-kpi-management/completeprogressreview/${contractid}`,
            "patch",
            {
              perspectives,
              rolloverresponsibilities: rolloverRows,
              trainingdevelopmentplan: trainingRows,
              appraisercomments,
              selfreview: selfReview,
            }
          );
          if (code === STATUS_CODE.SUCCESS) {
            setPopupOpen(true);
            setTimeout(() => {
              setPopupOpen(false);
              backToList();
            }, 1500);
          } else {
            message.error("Failed to complete progress review");
          }
        } catch (error) {
          console.error("Error completing progress review:", error);
          message.error("Failed to complete progress review");
        } finally {
          setSubmitLoading(false);
        }
      },
    });
  };

  return (
    <Wrap>
      <Spin spinning={loading}>
        <div className="breadcrumb mb-3">
          <p
            className="lite-text p-0 m-0"
            style={{ cursor: "pointer" }}
            onClick={backToList}
          >
            Performance Management
          </p>
          &nbsp;
          <RightOutlined className="mt-1" />
          &nbsp;
          <p className="m-0 p-0">Reviewing Progress</p>
        </div>

        <h6>
          {isReviewsClosed
            ? "Reviews Closed"
            : `${contract?.period || "Yearly"} Appraisal${
                canRespond && currentCheckpoint
                  ? ` – ${currentCheckpoint} Progress Review`
                  : lastCompletedCheckpoint
                  ? ` – Last Progress Review - ${lastCompletedCheckpoint.label}`
                  : ""
              }`}
        </h6>

        {isWaitingOnAppraisee && (
          <p className="lite-text">
            {contract?.fullname || "The appraisee"} completes their own progress reviews. Waiting for
            them to complete the {currentCheckpoint} progress review.
          </p>
        )}

        <Row gutter={[16, 16]} className="my-3">
          <Col xs={24} md={6}>
            <div className="lite-text">Name of the employee</div>
            <div className="text-bold">{contract?.fullname || "-"}</div>
          </Col>
          <Col xs={24} md={6}>
            <div className="lite-text">Period</div>
            <div className="text-bold">{contract?.period ? `${contract.period} Appraisal` : "-"}</div>
          </Col>
          <Col xs={24} md={6}>
            <div className="lite-text">Start date to attend appraisal</div>
            <div className="text-bold">{formatDate(contract?.startdate)}</div>
          </Col>
          <Col xs={24} md={6}>
            <div className="lite-text">Progress Review</div>
            <div className="text-bold">
              {(contract?.progressreviewdates || []).map((item, idx) => (
                <div key={idx}>
                  {item.label} – {formatDate(item.date)}
                </div>
              ))}
            </div>
          </Col>
        </Row>

        {isReviewsClosed ? (
          <>
            <Tabs activeKey={activeReviewTab} onChange={setActiveReviewTab} items={kpiModalTabItems} />

            <div className="perspective-title">Planning</div>
            <p className="lite-text">
              Objectives or other developmental activities to be undertaken by Appraisee during the
              appraisal period.
            </p>
            <Table
              columns={kpiModalColumns}
              dataSource={buildKpiModalRows(kpiModalGrouped)}
              pagination={false}
              rowKey={(row) => row.key}
              sticky={{ getContainer: () => document.querySelector(".ant-layout-content") }}
              rowClassName={(record) => (record.isGroupHeader ? "planning-group-row" : "")}
            />

            {(contract?.softskillsassessment || []).length > 0 && (
              <>
                <div className="perspective-title mt-4">Individual (soft skills) Assessment</div>
                <Table
                  columns={[
                    { title: "Objectives", dataIndex: "objectives", key: "objectives" },
                    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110 },
                  ]}
                  dataSource={contract.softskillsassessment}
                  pagination={false}
                  rowKey={(row) => `closed-softskills-${row.objectives}`}
                />
              </>
            )}

            {trainingRows.length > 0 && (
              <>
                <div className="perspective-title mt-4">Training & Development Plan</div>
                <Table
                  columns={buildTrainingColumns({ editable: false, checkpointKey: activeReviewTab })}
                  dataSource={trainingRows}
                  pagination={false}
                  rowKey={(row) => `closed-training-${row.sno}`}
                />
              </>
            )}

            <div className="perspective-title mt-4">{commentLabel}</div>
            <p>{appraisercomments[activeReviewTab] || "-"}</p>
          </>
        ) : (
          <>
            <h6 className="mt-4">Planning</h6>
            <p className="lite-text">
              Objectives or other developmental activities to be undertaken by Appraisee during the appraisal period.
            </p>

            <Table
              columns={planningColumns}
              dataSource={buildPlanningRows()}
              pagination={false}
              rowKey={(row) => row.key}
              sticky={{ getContainer: () => document.querySelector(".ant-layout-content") }}
              rowClassName={(record) => (record.isGroupHeader ? "planning-group-row" : "")}
            />

            {rolloverRows.length > 0 && (
              <>
                <div className="perspective-title">Rollover Responsibility from Previous Appraisal</div>
                <Table
                  columns={rolloverColumns}
                  dataSource={rolloverRows}
                  pagination={false}
                  rowKey={(row) => `rollover-${row.sno}`}
                />
              </>
            )}

            {trainingRows.length > 0 && (
              <>
                <div className="perspective-title mt-4">Training & Development Plan</div>
                <p className="lite-text">
                  Based on the strengths and limitations identified in the previous review, indicate the
                  work experience and/or training suggested during the plan period.
                </p>
                <Table
                  columns={buildTrainingColumns({ editable: canRespond, checkpointKey: viewCheckpoint })}
                  dataSource={trainingRows}
                  pagination={false}
                  rowKey={(row) => `training-${row.sno}`}
                />
              </>
            )}

            <div className="perspective-title mt-4">{commentLabel}</div>
            {canRespond ? (
              <TextArea
                autoSize={{ minRows: 1, maxRows: 4 }}
                value={appraisercomments[currentCheckpoint] || ""}
                onChange={(e) =>
                  setAppraisercomments((prev) => ({ ...prev, [currentCheckpoint]: e.target.value }))
                }
              />
            ) : (
              <p>{appraisercomments[viewCheckpoint] || "-"}</p>
            )}

            <UserFloatingButton
              setIsViewKpi={setKpiViewOpen}
              bottompossition={canRespond ? "90px" : "20px"}
              icon={userFloatCycle}
            />
          </>
        )}

        <div className="footer">
          {contract?.id && <KpiHistoryBadge contractid={contract.id} />}
          {canRespond && (
            <>
              <ButtonOutlined onClick={backToList}>
                Cancel
              </ButtonOutlined>
              <ButtonFilled type="primary" disabled={submitLoading} onClick={onComplete} className="ms-2">
                Complete
              </ButtonFilled>
            </>
          )}
        </div>
      </Spin>

      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type="success"
        message="Completed"
      />

      <Modal
        title="KPI Planning & Progress Reviews"
        open={kpiViewOpen}
        onCancel={() => setKpiViewOpen(false)}
        footer={null}
        width={1100}
        styles={{ body: { maxHeight: "75vh", overflowY: "auto" } }}
        centered
      >
        <div style={{ background: "#eef1fb", borderRadius: 8, padding: "16px 24px", marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col flex="0 0 auto">
              <Avatar size={64} src={appraiseeUser?.profileurl} icon={<UserOutlined />} />
            </Col>
            <Col flex="0 0 220px">
              <div className="text-bold">{contract?.fullname || "-"}</div>
              <div className="lite-text">{appraiseeUser?.jobtitle || "-"}</div>
            </Col>
            <Col flex="1 1 0">
              <div className="lite-text">
                Employee ID: <span className="text-bold">{contract?.employeeid || "-"}</span>
              </div>
              <div className="lite-text mt-1">
                Location: <span className="text-bold">{appraiseeLocation || "-"}</span>
              </div>
            </Col>
            <Col flex="1 1 0">
              <div className="lite-text">
                Department: <span className="text-bold">{appraiseeDepartment || "-"}</span>
              </div>
              <div className="lite-text mt-1">
                Hired Date: <span className="text-bold">{formatDate(appraiseeUser?.hireddate)}</span>
              </div>
            </Col>
          </Row>
        </div>

        <Row gutter={[16, 16]} className="mb-3">
          <Col xs={24} md={12}>
            <div className="lite-text">Start date to attend appraisal</div>
            <div className="text-bold">{formatDate(contract?.startdate)}</div>
          </Col>
          <Col xs={24} md={12}>
            <div className="lite-text">KPI defined by</div>
            <div className="text-bold">
              {contract?.kpidefinedby === "Self"
                ? `Appraisee : ${contract?.fullname || "-"}`
                : `Appraiser : ${contract?.kpidefinedby || "-"}`}
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="lite-text">Period</div>
            <div className="text-bold">{contract?.period || "-"}</div>
          </Col>
          <Col xs={24} md={12}>
            <div className="lite-text">Progress Review</div>
            {(contract?.progressreviewdates || []).map((item) => (
              <div className="text-bold" key={item.label}>
                {item.label} – {formatDate(item.date)}
              </div>
            ))}
          </Col>
        </Row>

        <Tabs activeKey={activeReviewTab} onChange={setActiveReviewTab} items={kpiModalTabItems} />

        <div className="perspective-title">Planning</div>
        <p className="lite-text">
          Objectives or other developmental activities to be undertaken by Appraisee during the
          appraisal period.
        </p>
        <Table
          columns={kpiModalColumns}
          dataSource={buildKpiModalRows(kpiModalGrouped)}
          pagination={false}
          rowKey={(row) => row.key}
          sticky={{ getContainer: () => document.querySelector(".ant-layout-content") }}
          rowClassName={(record) => (record.isGroupHeader ? "planning-group-row" : "")}
        />

        {(contract?.softskillsassessment || []).length > 0 && (
          <>
            <div className="perspective-title mt-4">Individual (soft skills) Assessment</div>
            <Table
              columns={[
                { title: "Objectives", dataIndex: "objectives", key: "objectives" },
                { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110 },
              ]}
              dataSource={contract.softskillsassessment}
              pagination={false}
              rowKey={(row) => `modal-softskills-${row.objectives}`}
            />
          </>
        )}

        {trainingRows.length > 0 && (
          <>
            <div className="perspective-title mt-4">Training & Development Plan</div>
            <Table
              columns={buildTrainingColumns({ editable: false, checkpointKey: activeReviewTab })}
              dataSource={trainingRows}
              pagination={false}
              rowKey={(row) => `modal-training-${row.sno}`}
            />
          </>
        )}

        <div className="perspective-title mt-4">{commentLabel}</div>
        <p>{appraisercomments[activeReviewTab] || "-"}</p>
      </Modal>
    </Wrap>
  );
};

export default ReviewProgress;